import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
import { useEffect, useMemo, useState } from "react";
import { useReadDatasourceQuery } from "store/api";
import { buildWidgetPreviewRequest, extractPreviewRows, type DataSourceRecord, useDatasourceOptions } from "./";

type Props = {
  onBack: () => void;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  initialData?: Record<string, any> | null;
};

type TableWidgetField = {
  field: string;
  display: boolean;
  edit: boolean;
  link: boolean;
  file: boolean;
  linkValue: string;
  linkField: string;
  linkOwnField: string;
};

const createDefaultField = (fieldName: string): TableWidgetField => ({
  field: fieldName,
  display: fieldName !== "id",
  edit: fieldName !== "id",
  link: false,
  file: false,
  linkValue: "",
  linkField: "",
  linkOwnField: fieldName,
});

const toBooleanFlag = (value: unknown, defaultValue: boolean) => {
  if (value === undefined || value === null) return defaultValue;
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  return defaultValue;
};

const normalizeConfiguredFields = (rawFields: unknown[]): TableWidgetField[] => {
  return rawFields
    .map((entry) => {
      if (typeof entry === "string") {
        return createDefaultField(entry);
      }
      if (!entry || typeof entry !== "object") return null;

      const record = entry as Record<string, any>;
      const fieldName = String(record.field ?? "").trim();
      if (!fieldName) return null;

      const display = toBooleanFlag(record.display, true);
      const link = toBooleanFlag(record.link, false);
      const file = toBooleanFlag(record.file, false);
      const edit = display ? toBooleanFlag(record.edit, true) : false;

      return {
        field: fieldName,
        display,
        edit,
        link,
        file: link ? false : file,
        linkValue: String(record.linkValue ?? ""),
        linkField: String(record.linkField ?? ""),
        linkOwnField: String(record.linkOwnField ?? fieldName),
      } satisfies TableWidgetField;
    })
    .filter((value): value is TableWidgetField => value != null)
    .map((item) => {
      if (item.file && item.link) {
        return { ...item, file: false };
      }
      return item;
    });
};

const mergeFieldsWithPreview = (previewFieldNames: string[], currentFields: TableWidgetField[]): TableWidgetField[] => {
  const byField = new Map(currentFields.map((item) => [item.field, item]));
  return previewFieldNames.map((fieldName) => {
    const existing = byField.get(fieldName);
    if (!existing) return createDefaultField(fieldName);
    return {
      ...existing,
      linkOwnField: existing.linkOwnField || fieldName,
      edit: existing.display === false ? false : existing.edit,
    };
  });
};

const TableWidgetForm: React.FC<Props> = ({ onBack, onSubmit: onSubmitProp, initialData }) => {
  const [title, setTitle] = useState("");
  const [datasource, setDatasource] = useState("");
  const [fields, setFields] = useState<TableWidgetField[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setTitle(String(initialData?.title ?? ""));
    setDatasource(String(initialData?.table ?? ""));

    const rawFields = Array.isArray(initialData?.fields) ? initialData.fields : [];
    setFields(normalizeConfiguredFields(rawFields));
  }, [initialData]);

  const { dataSources, datasourceOptions } = useDatasourceOptions();

  const previewRequest = useMemo(() => {
    if (!datasource) return null;
    const ds = (Array.isArray(dataSources) ? dataSources : []).find(
      (item: DataSourceRecord) => String(item?.id ?? "") === String(datasource)
    ) as DataSourceRecord | undefined;

    return buildWidgetPreviewRequest(ds);
  }, [datasource, dataSources]);

  const { data: previewData } = useReadDatasourceQuery(previewRequest as any, {
    skip: !previewRequest,
  });

  const previewRows = useMemo(() => extractPreviewRows(previewData), [previewData]);

  const previewFields = useMemo(() => {
    const rows = previewRows;
    if (!rows.length || typeof rows[0] !== "object" || rows[0] == null) return [] as string[];
    return Object.keys(rows[0]);
  }, [previewRows]);

  const datasourceHasId = useMemo(() => {
    if (!previewRows.length) return true;
    return previewRows.every((row) => {
      if (!row || typeof row !== "object") return false;
      const idValue = row.id;
      return idValue !== undefined && idValue !== null && String(idValue).trim() !== "";
    });
  }, [previewRows]);

  useEffect(() => {
    if (!previewFields.length) return;
    setFields((current) => mergeFieldsWithPreview(previewFields, current));
  }, [previewFields]);

  const toggleFieldSetting = (index: number, setting: "display" | "edit" | "file" | "link") => {
    setFields((current) =>
      current.map((item, idx) => {
        if (idx !== index) return item;

        if (setting === "display") {
          if (item.field === "id") return item;
          const nextDisplay = !item.display;
          return {
            ...item,
            display: nextDisplay,
            edit: nextDisplay ? item.edit : false,
          };
        }

        if (setting === "edit") {
          if (!item.display) return item;
          return { ...item, edit: !item.edit };
        }

        if (setting === "file") {
          const nextFile = !item.file;
          return {
            ...item,
            file: nextFile,
            link: nextFile ? false : item.link,
          };
        }

        const nextLink = !item.link;
        return {
          ...item,
          link: nextLink,
          file: nextLink ? false : item.file,
          linkOwnField: item.linkOwnField || item.field,
        };
      })
    );
  };

  const updateLinkField = (index: number, key: "linkValue" | "linkField" | "linkOwnField", value: string) => {
    setFields((current) =>
      current.map((item, idx) => (idx === index ? { ...item, [key]: value } : item))
    );
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const sanitizedFields = fields.map((item) => ({
        field: String(item.field).trim(),
        display: toBooleanFlag(item.display, true),
        edit: toBooleanFlag(item.edit, true),
        link: toBooleanFlag(item.link, false),
        file: toBooleanFlag(item.file, false),
        linkValue: String(item.linkValue ?? "").trim(),
        linkField: String(item.linkField ?? "").trim(),
        linkOwnField: String(item.linkOwnField ?? item.field).trim(),
      }));


      await onSubmitProp({
        type: "TableWidget",
        title,
        table: datasource,
        fields: sanitizedFields,
      });
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formRowStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "28px minmax(120px, 1fr) minmax(120px, 1fr) 160px minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr)",
    gap: 8,
    alignItems: "start",
    padding: "8px 10px",
    borderBottom: "1px solid #e7ebef",
  };

  const getActionButtonStyle = (active: boolean, disabled = false): React.CSSProperties => ({
    color: active ? "var(--main-color, #e95b45)" : "var(--grey-color-mid, #8f99a3)",
    borderColor: active ? "var(--main-color, #e95b45)" : "rgba(143, 153, 163, 0.4)",
    background: active ? "var(--main-color-lighter, #fff1ee)" : "#ffffff",
    opacity: disabled ? 0.45 : 1,
  });

  return (
    <>
      <group data-gap="20" data-max-length="500">
        <group data-length="autofit">
          <Input
            dataLength="autofit"
            size="large"
            type="text"
            label="title"
            name="table_widget_title"
            value={title}
            onChange={(e: any) => setTitle(String(e.target?.value ?? ""))}
          />
        </group>
      </group>

      <group data-gap="20" data-max-length="500">
        <group data-length="autofit">
          <Input
            dataLength="autofit"
            size="large"
            type={InputType.Select}
            label="datasources"
            name="table_widget_datasource"
            value={datasource}
            options={datasourceOptions}
            onChange={(v: any) => {
              setDatasource(String(v ?? ""));
              setFields([]);
            }}
          />
        </group>
      </group>

      {fields.length > 0 && (
        <group data-direction="column" data-gap="8" data-max-length="1040">
          <text data-weight="600">fields</text>
          <group data-direction="column" data-border="outline" data-radius="5" data-background="main-background" data-wrap="no" style={{ overflowX: "auto" }}>
            <group
              data-background="main-lighter"
              data-wrap="no"
              data-align="center"
              style={{
                ...formRowStyle,
                fontWeight: 600,
              }}
            >
              <div />
              <text data-color="main" data-weight="600">field</text>
              <text data-color="main" data-weight="600">source</text>
              <text data-color="main" data-weight="600">actions</text>
              <text data-color="main" data-weight="600">used_field</text>
              <text data-color="main" data-weight="600">screen</text>
              <text data-color="main" data-weight="600">dest_field</text>
            </group>

            {fields.map((field, index) => (
              <group key={field.field} data-wrap="no" style={formRowStyle}>
                <group data-align="center" data-justify="center" data-color="grey-color-mid" data-height="28">
                  <icon data-icon-size="small">drag_indicator</icon>
                </group>

                <group data-align="center" data-height="28">
                  <text data-color="main" data-weight="600">{field.field}</text>
                </group>
                <group data-align="center" data-height="28">
                  <text data-color="grey-color-mid">{field.field}</text>
                </group>

                <group data-wrap="no" data-gap="6" data-align="center" data-height="28">
                  <Button
                    type="button"
                    mini
                    outline
                    style={getActionButtonStyle(field.display, field.field === "id")}
                    icon={field.display ? "visibility" : "visibility_off"}
                    title="display"
                    disabled={field.field === "id"}
                    onClick={() => {
                      if (field.field === "id") return;
                      toggleFieldSetting(index, "display");
                    }}
                  />
                  <Button
                    type="button"
                    mini
                    outline
                    style={getActionButtonStyle(field.edit)}
                    icon="edit"
                    title="editable"
                    onClick={() => toggleFieldSetting(index, "edit")}
                  />
                  <Button
                    type="button"
                    mini
                    outline
                    style={getActionButtonStyle(field.file)}
                    icon="description"
                    title="file"
                    onClick={() => toggleFieldSetting(index, "file")}
                  />
                  <Button
                    type="button"
                    mini
                    outline
                    style={getActionButtonStyle(field.link)}
                    icon="open_in_new"
                    title="link"
                    onClick={() => toggleFieldSetting(index, "link")}
                  />
                </group>

                {field.link ? (
                  <Input
                    dataLength="autofit"
                    size="small"
                    type={InputType.Select}
                    name={`table_field_linkOwnField_${field.field}`}
                    value={field.linkOwnField}
                    options={fields.map((item) => ({ value: item.field, text: item.field }))}
                    onChange={(v: any) => updateLinkField(index, "linkOwnField", String(v ?? ""))}
                  />
                ) : (
                  <div />
                )}

                {field.link ? (
                  <Input
                    dataLength="autofit"
                    size="small"
                    type="text"
                    name={`table_field_linkValue_${field.field}`}
                    value={field.linkValue}
                    onChange={(e: any) => updateLinkField(index, "linkValue", String(e.target?.value ?? ""))}
                  />
                ) : (
                  <div />
                )}

                {field.link ? (
                  <Input
                    dataLength="autofit"
                    size="small"
                    type="text"
                    name={`table_field_linkField_${field.field}`}
                    value={field.linkField}
                    onChange={(e: any) => updateLinkField(index, "linkField", String(e.target?.value ?? ""))}
                  />
                ) : (
                  <div />
                )}
              </group>
            ))}
          </group>
        </group>
      )}

      {!datasourceHasId && (
        <text data-color="red">
          Selected datasource rows must include id. Old back_office crashes with "key is required" when id is missing.
        </text>
      )}

      <group data-wrap="no" data-gap="10">
        <Button
          type="button"
          primary
          text="add"
          onClick={() => {
            if (isLoading || !datasourceHasId) return;
            void handleSubmit();
          }}
        />
        <Button
          type="button"
          text="back"
          onClick={() => {
            if (isLoading) return;
            onBack();
          }}
        />
      </group>
    </>
  );
};

export default TableWidgetForm;
